import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export type PlanCodeItem = {
  phase: StringUnion;
  planCode: StringUnion;
  description: StringUnion;
  units: number;
  tracInputL70: StringUnion;
  onTheJobRiderBenefit: number;
};

type PlanCodeProp = {
  phase: StringUnion;
  planCode: StringUnion;
  description: StringUnion;
  units: number;
  tracInputL70: StringUnion;
  onTheJobRiderBenefit: number;
};

export class UnvalidatedPlanCode {
  public readonly key = 'UnvalidatedPlanCode';

  constructor(
    public readonly phase: StringUnion,
    public readonly planCode: StringUnion,
    public readonly description: StringUnion,
    public readonly units: number,
    public readonly tracInputL70: StringUnion,
    public readonly onTheJobRiderBenefit: number
  ) {}
}

export class InvalidPlanCode {
  public readonly key = 'InvalidPlanCode';

  constructor(
    public readonly phase: StringUnion,
    public readonly planCode: StringUnion,
    public readonly description: StringUnion,
    public readonly units: number,
    public readonly tracInputL70: StringUnion,
    public readonly onTheJobRiderBenefit: number
  ) {}
}

export class ValidPlanCode {
  public static createFromApp({phase, planCode, description, units, tracInputL70, onTheJobRiderBenefit}: PlanCodeProp) {
    return this.create(phase, planCode, description, units, tracInputL70, onTheJobRiderBenefit);
  }

  public static create(
    phase: StringUnion,
    planCode: StringUnion,
    description: StringUnion,
    units: number,
    tracInputL70: StringUnion,
    onTheJobRiderBenefit: number
  ) {
    return !isNaN(units)
      ? new ValidPlanCode(phase, planCode, description, units, tracInputL70, onTheJobRiderBenefit)
      : new InvalidPlanCode(phase, planCode, description, units, tracInputL70, onTheJobRiderBenefit);
  }

  public readonly key = 'ValidPlanCode';
  private constructor(
    public readonly phase: StringUnion,
    public readonly planCode: StringUnion,
    public readonly description: StringUnion,
    public readonly units: number,
    public readonly tracInputL70: StringUnion,
    public readonly onTheJobRiderBenefit: number
  ) {}
}

export type PlanCode = UnvalidatedPlanCode | InvalidPlanCode | ValidPlanCode;

export type PlanCodeOptions<TReturn> = ToPartialMatchOptions<PlanCode, 'key', TReturn>;

export const matchPlanCode = <TReturn>(options: PlanCodeOptions<TReturn>) => (app: PlanCode): TReturn => {
  switch (app.key) {
    case 'InvalidPlanCode':
      return options.InvalidPlanCode ? options.InvalidPlanCode(app) : options.default(app);
    case 'UnvalidatedPlanCode':
      return options.UnvalidatedPlanCode ? options.UnvalidatedPlanCode(app) : options.default(app);
    case 'ValidPlanCode':
      return options.ValidPlanCode ? options.ValidPlanCode(app) : options.default(app);
  }
};

export const changeUnits = (units: number) => (app: PlanCode) => {
  return ValidPlanCode.createFromApp({...app, units});
};

export const updateItem = (phase: string, planCode: string, description: string, units: number, onTheJobRiderBenefit: number) => (app: PlanCode) => {
  return ValidPlanCode.createFromApp({
    ...app,
    phase: NonEmptyString.create(phase),
    planCode: NonEmptyString.create(planCode),
    description: NonEmptyString.create(description),
    units,
    onTheJobRiderBenefit,
  });
};

export const EmptyPlanCode = new UnvalidatedPlanCode(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  0,
  new EmptyString(),
  0
);
